/**
 * Pure list logic of the Telegram accounts table (KAN-614):
 * enabled-first sort, filters, summary, frozen auto-disable text.
 * KAN-779: sort by connection date, remembered in localStorage.
 */
import type { TelegramAccount } from '@/types/telegramAccount';
import { isOutreachPaused } from '@/lib/telegramAccountFormat';
import { loadJson, saveJson } from '@/lib/persistedState';

export type AccountFilter = 'all' | 'enabled' | 'disabled' | 'problem' | 'decommissioned' | 'healthy' | 'flooded';

/** KAN-779: порядок строк таблицы. */
export type AccountSort = 'enabled_first' | 'connected_desc' | 'connected_asc';

export const ACCOUNT_SORTS: ReadonlyArray<{ value: AccountSort; label: string }> = [
  { value: 'enabled_first', label: 'Включённые первыми' },
  { value: 'connected_desc', label: 'Сначала новые' },
  { value: 'connected_asc', label: 'Сначала старые' },
];

export const DEFAULT_ACCOUNT_SORT: AccountSort = 'enabled_first';

/** Ключ localStorage с выбранной сортировкой (на браузер, не на пользователя). */
export const ACCOUNT_SORT_STORAGE_KEY = 'telegramAccounts.sort';

export const ACCOUNT_FILTERS: ReadonlyArray<{ value: AccountFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'enabled', label: 'Включены' },
  { value: 'disabled', label: 'Выключены' },
  { value: 'problem', label: 'Проблемные' },
  { value: 'decommissioned', label: 'Списанные' },
  { value: 'healthy', label: 'Здоровые' },
  { value: 'flooded', label: 'Были под флудом' },
];

export const FROZEN_AUTO_DISABLED_TEXT = 'Заморожен Telegram, выключен автоматически';

export interface AccountsSummaryData {
  total: number;
  enabled: number;
  /** Пишут первыми: см. canWriteFirst (KAN-699). */
  outreach: number;
  disabled: number;
  problem: number;
  decommissioned: number;
  /** KAN-997: здоровые (поле healthy бэкенда), без списанных. */
  healthy: number;
  /** KAN-997: были под флудом, без списанных. */
  flooded: number;
  /** KAN-997: статус connected, без списанных. */
  connected: number;
}

/** Включённые выше выключенных; порядок внутри групп сохраняется, вход не мутируется. */
export function sortEnabledFirst(accounts: TelegramAccount[]): TelegramAccount[] {
  return [
    ...accounts.filter((a) => a.enabled),
    ...accounts.filter((a) => !a.enabled),
  ];
}

/** Дата подключения: connected_at, иначе imported_at (старые записи), иначе null. */
export function connectedAt(account: TelegramAccount): string | null {
  return account.connected_at || account.imported_at || null;
}

/** ISO-строка в миллисекунды; null для пустой или нечитаемой даты. */
function connectedAtMs(account: TelegramAccount): number | null {
  const iso = connectedAt(account);
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Сортировка по дате подключения. Аккаунты без даты всегда в конце в исходном
 * порядке; при равной дате порядок исходный (Array.prototype.sort стабилен).
 * Вход не мутируется.
 */
function sortByConnectedAt(
  accounts: TelegramAccount[],
  direction: 'asc' | 'desc'
): TelegramAccount[] {
  const sign = direction === 'desc' ? -1 : 1;
  const dated = accounts.filter((a) => connectedAtMs(a) !== null);
  const undated = accounts.filter((a) => connectedAtMs(a) === null);
  dated.sort((a, b) => sign * ((connectedAtMs(a) as number) - (connectedAtMs(b) as number)));
  return [...dated, ...undated];
}

/** Применяет выбранную сортировку; enabled_first = sortEnabledFirst. */
export function sortAccounts(accounts: TelegramAccount[], sort: AccountSort): TelegramAccount[] {
  switch (sort) {
    case 'connected_desc':
      return sortByConnectedAt(accounts, 'desc');
    case 'connected_asc':
      return sortByConnectedAt(accounts, 'asc');
    default:
      return sortEnabledFirst(accounts);
  }
}

export function isAccountSort(value: unknown): value is AccountSort {
  return ACCOUNT_SORTS.some((s) => s.value === value);
}

/** Сортировка из localStorage; мусор, чужое значение или недоступное хранилище дают дефолт. */
export function readAccountSort(): AccountSort {
  const stored = loadJson(ACCOUNT_SORT_STORAGE_KEY);
  return isAccountSort(stored) ? stored : DEFAULT_ACCOUNT_SORT;
}

/** Запоминает сортировку; ошибки хранилища глушатся. */
export function writeAccountSort(sort: AccountSort): void {
  saveJson(ACCOUNT_SORT_STORAGE_KEY, sort);
}

/** Мёртвая сессия, заморозка, неудачная проверка или автоотключение по заморозке. */
export function isProblemAccount(account: TelegramAccount): boolean {
  if (account.decommissioned) return false;
  return (
    account.status === 'dead' ||
    account.health === 'dead' ||
    account.health === 'frozen' ||
    (!account.enabled && account.disabled_reason === 'frozen') ||
    isOutreachPaused(account)
  );
}

/** Может писать первым (KAN-699): не списан, включён, без проблем, режим outreach. */
export function canWriteFirst(account: TelegramAccount): boolean {
  return (
    !account.decommissioned &&
    account.enabled &&
    account.outreach &&
    !isProblemAccount(account)
  );
}

export function filterAccounts(
  accounts: TelegramAccount[],
  filter: AccountFilter
): TelegramAccount[] {
  if (filter === 'decommissioned') return accounts.filter((a) => a.decommissioned);
  const active = accounts.filter((a) => !a.decommissioned);
  switch (filter) {
    case 'enabled':
      return active.filter((a) => a.enabled);
    case 'disabled':
      return active.filter((a) => !a.enabled);
    case 'problem':
      return active.filter(isProblemAccount);
    case 'healthy':
      return active.filter((a) => a.healthy === true);
    case 'flooded':
      return active.filter((a) => a.flood_flagged === true);
    default:
      return active;
  }
}

export function summarizeAccounts(accounts: TelegramAccount[]): AccountsSummaryData {
  const active = accounts.filter((a) => !a.decommissioned);
  const enabled = active.filter((a) => a.enabled).length;
  return {
    total: active.length,
    enabled,
    outreach: active.filter(canWriteFirst).length,
    disabled: active.length - enabled,
    problem: active.filter(isProblemAccount).length,
    decommissioned: accounts.length - active.length,
    healthy: active.filter((a) => a.healthy === true).length,
    flooded: active.filter((a) => a.flood_flagged === true).length,
    connected: active.filter((a) => a.status === 'connected').length,
  };
}

/** Знаменатель подписи "показано N из M": списанные считаются от числа списанных. */
export function shownTotal(summary: AccountsSummaryData, filter: AccountFilter): number {
  return filter === 'decommissioned' ? summary.decommissioned : summary.total;
}

export function frozenAutoDisabledText(account: TelegramAccount): string | null {
  return !account.decommissioned && !account.enabled && account.disabled_reason === 'frozen'
    ? FROZEN_AUTO_DISABLED_TEXT
    : null;
}
